import { RegisterDto, LoginDto, ForgotPasswordDto, ResetPasswordDto } from '@/lib/validations/auth.validation';
import { User, UserResponse } from '@/models/User';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

interface AuthResponse {
  access_token: string;
  user: UserResponse;
  message?: string;
}

interface ApiError {
  message: string;
  statusCode?: number;
  errors?: Record<string, string[]>;
}

class AuthService {
  private async handleResponse<T>(response: Response): Promise<T> {
    const data = await response.json();
    
    if (!response.ok) {
      const error: ApiError = {
        message: data.message || 'An error occurred',
        statusCode: response.status,
        errors: data.errors,
      };
      throw error;
    }
    
    return data;
  }

  async register(dto: RegisterDto): Promise<{ message: string; user: UserResponse }> {
    const response = await fetch(`${API_BASE_URL}/users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: dto.email,
        password: dto.password,
        firstName: dto.firstName,
        lastName: dto.lastName,
        companyName: dto.companyName,
        role: dto.role,
      }),
    });

    return this.handleResponse<{ message: string; user: UserResponse }>(response);
  }

  async login(dto: LoginDto): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: dto.email,
        password: dto.password,
      }),
      credentials: 'include',
    });

    const data = await this.handleResponse<AuthResponse>(response);
    
    if (data.access_token) {
      // Always store in localStorage for persistence across sessions
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    
    return data;
  }

  async logout(): Promise<void> {
    const token = this.getToken();
    
    if (token) {
      try {
        // Call backend logout endpoint to clear token from database
        await fetch(`${API_BASE_URL}/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    // Clear all auth data from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    
    // Clear ALL localStorage items (in case there are other stored items)
    localStorage.clear();
    
    // Clear sessionStorage as well
    sessionStorage.clear();
    
    // Clear service worker caches
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    
    // Redirect to login with cache-busting query param
    if (typeof window !== 'undefined') {
      window.location.href = `/auth/login?t=${Date.now()}`;
    }
  }

  async forgotPassword(dto: ForgotPasswordDto): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });

    return this.handleResponse<{ message: string }>(response);
  }

  async resetPassword(email: string, dto: ResetPasswordDto): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        newPassword: dto.password,
      }),
    });

    return this.handleResponse<{ message: string }>(response);
  }

  async verifyOtp(email: string, otp: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        otp,
      }),
    });

    return this.handleResponse<{ message: string }>(response);
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/verify-email?token=${token}`, {
      method: 'GET',
    });

    return this.handleResponse<{ message: string }>(response);
  }

  async getUserProfile(): Promise<UserResponse> {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_BASE_URL}/users/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.logout();
        throw new Error('Session expired. Please login again.');
      }
      throw new Error('Failed to fetch user profile');
    }

    const userData = await this.handleResponse<UserResponse>(response);
    
    // Update stored user data
    localStorage.setItem('user', JSON.stringify(userData));
    
    return userData;
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  getStoredUser(): UserResponse | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getUserRole(): string | null {
    const user = this.getStoredUser();
    return user?.role || null;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });

    return this.handleResponse<{ message: string }>(response);
  }
}

export const authService = new AuthService();
export default authService;
